import { useRef } from 'react';
import { Reference } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';
import { DraggableLocation, DropResult } from '@hello-pangea/dnd';

import { useSnackbar } from 'common/hooks/useSnackbar';
import {
    DevelopmentItemGroupFragmentDoc,
    IDevelopmentItemGroupFragment,
    UpdateDevelopmentItemGroupDocument,
    UpdateDevelopmentItemGroupsDocument,
} from 'graphql/types';
import { findNestedDraggable, reorder } from 'common/utils/draggable';

export const useDevelopmentPlanDND = (
    groups: IDevelopmentItemGroupFragment[],
    setGroups: (groups: IDevelopmentItemGroupFragment[]) => void
) => {
    const [translate] = useTranslation();
    const [displaySnackbar] = useSnackbar();
    // A fallback for if the sorting process fails
    const previousGroups = useRef<IDevelopmentItemGroupFragment[] | undefined>(
        undefined
    );

    const [updateGroupsOrder] = useMutation(
        UpdateDevelopmentItemGroupsDocument
    );
    const [updateGroup] = useMutation(UpdateDevelopmentItemGroupDocument);

    const handleError = () => {
        displaySnackbar(translate('sortingFail'));

        if (!previousGroups.current) return;

        // Rollback of the groups
        setGroups(previousGroups.current);
    };

    const handleDragEndGroups = (
        source: DraggableLocation,
        destination?: DraggableLocation
    ) => {
        if (!destination) return;

        previousGroups.current = [...groups];

        const orderedGroups = reorder<IDevelopmentItemGroupFragment>(
            groups.filter((group) => !group.isInbox),
            source.index,
            destination.index
        );

        const inboxGroupResult = groups.filter((group) => group.isInbox);

        const newGroups = inboxGroupResult.length
            ? [inboxGroupResult[0], ...orderedGroups]
            : orderedGroups;

        setGroups(newGroups);

        updateGroupsOrder({
            variables: {
                orderIds: orderedGroups.map((group) => group.id),
            },
            optimisticResponse: {
                updateDevelopmentItemGroups: {
                    __typename: 'UpdateDevelopmentItemGroups',
                    ok: true,
                },
            },
        }).catch(handleError);
    };

    const handleDragEndItems = (
        source: DraggableLocation,
        draggableId: string,
        destination?: DraggableLocation
    ) => {
        if (!destination) return;

        const draggableItem = findNestedDraggable(
            groups,
            'developmentItems',
            draggableId
        );

        if (!draggableItem) return;

        previousGroups.current = [...groups];

        groups.forEach((group) => {
            if (
                source.droppableId !== group.id &&
                destination.droppableId !== group.id
            ) {
                return group;
            }

            const items = group.developmentItems.filter(
                (item) => item.id !== draggableId
            );

            if (group.id === destination.droppableId) {
                items.splice(destination.index, 0, draggableItem);

                updateGroup({
                    variables: {
                        id: destination.droppableId,
                        developmentItemGroup: {
                            itemOrder: items.map((item) => item.id),
                        },
                    },
                    optimisticResponse: {
                        updateDevelopmentItemGroup: {
                            __typename: 'UpdateDevelopmentItemGroup',
                            developmentItemGroup: {
                                ...group,
                                developmentItems: items,
                            },
                        },
                    },
                    update: (cache) => {
                        if (source.droppableId === destination.droppableId) {
                            return;
                        }

                        const previousDevelopmentItemGroup = cache.readFragment(
                            {
                                id: `DevelopmentItemGroup:${source.droppableId}`,
                                fragment: DevelopmentItemGroupFragmentDoc,
                                fragmentName: 'DevelopmentItemGroup',
                            }
                        );

                        if (!previousDevelopmentItemGroup) return;

                        // Remove moved development item from previous owning group
                        cache.modify({
                            id: `DevelopmentItemGroup:${source.droppableId}`,
                            fields: {
                                developmentItems(
                                    existingDevelopmentItemRefs: readonly (
                                        | Reference
                                        | IDevelopmentItemGroupFragment
                                    )[] = [],
                                    { readField }
                                ) {
                                    return existingDevelopmentItemRefs.filter(
                                        (itemRef) => {
                                            const itemId = readField(
                                                'id',
                                                itemRef
                                            );

                                            return itemId !== draggableId;
                                        }
                                    );
                                },
                            },
                        });
                    },
                }).catch(handleError);
            }
        });
    };

    const handleDragEnd = ({
        type,
        destination,
        source,
        draggableId,
    }: DropResult) => {
        if (type === 'GROUP') {
            return handleDragEndGroups(source, destination || undefined);
        }

        handleDragEndItems(source, draggableId, destination || undefined);
    };

    return { handleDragEnd };
};
